import React, { useEffect, useState, useMemo } from 'react';
import './AdminDashboard.css';
import { getAllOrders, markOrderComplete } from '../api/order';
import { resolveImageUrl } from '../utils/image';
import AdminNav from './AdminNav';

export default function AdminDashboard() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'complete'>('all');
  const [completingId, setCompletingId] = useState<number | null>(null);

  const fetchOrdersList = async () => {
    try {
      setLoading(true);
      const stored = localStorage.getItem('user');
      const parsed = stored ? JSON.parse(stored) : null;
      const userId = parsed?.userId || parsed?.UserId || parsed?.id || 0;
      const data = await getAllOrders(Number(userId));
      setOrders(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrdersList();
  }, []);

  const handleMarkComplete = async (orderId: number) => {
    try {
      setCompletingId(orderId);
      const stored = localStorage.getItem('user');
      const parsed = stored ? JSON.parse(stored) : null;
      const adminUserId = parsed?.userId || parsed?.UserId || parsed?.id || 0;
      await markOrderComplete(Number(adminUserId), Number(orderId));
      
      // Update local state
      setOrders(prev => prev.map(o => o.orderId === orderId ? { ...o, isComplete: true, status: 'Complete' } : o));
      if (selectedOrder && selectedOrder.orderId === orderId) {
        setSelectedOrder((prev: any) => ({ ...prev, isComplete: true, status: 'Complete' }));
      }
    } catch (err) {
      alert('Failed to mark order as complete.');
    } finally {
      setCompletingId(null);
    }
  };

  // KPIs
  const totalOrdersCount = orders.length;
  const completedOrdersCount = orders.filter(o => o.isComplete || o.status === 'Complete').length;
  const pendingOrdersCount = totalOrdersCount - completedOrdersCount;
  const totalRevenue = orders.reduce((sum, o) => {
    const val = typeof o.totalPrice === 'number' ? o.totalPrice : parseFloat(String(o.totalPrice || 0).replace(/[^0-9.-]+/g, ''));
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Status filter
      const isComplete = order.isComplete || order.status === 'Complete';
      if (statusFilter === 'pending' && isComplete) return false;
      if (statusFilter === 'complete' && !isComplete) return false;

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const idMatch = String(order.orderId).toLowerCase().includes(q);
        const nameMatch = (order.userName || '').toLowerCase().includes(q);
        const phoneMatch = (order.phoneNumber || '').toLowerCase().includes(q);
        const cityMatch = (order.city || '').toLowerCase().includes(q);
        return idMatch || nameMatch || phoneMatch || cityMatch;
      }

      return true;
    });
  }, [orders, statusFilter, searchQuery]);

  return (
    <div className="ad-page-wrapper">
      <AdminNav activeTab="orders" />

      <main className="ad-main-content">
        <div className="ad-container">
          {/* Page Title & Refresh */}
          <div className="ad-page-header">
            <div>
              <h1 className="ad-page-title">Orders Management</h1>
              <p className="ad-page-subtitle">Track customer orders, confirm advance payments, and manage order bookings</p>
            </div>
            <button className="ad-btn-refresh" onClick={fetchOrdersList} disabled={loading}>
              <span>🔄</span> {loading ? 'Refreshing...' : 'Refresh Orders'}
            </button>
          </div>

          {/* Stats KPI Cards */}
          <div className="ad-stats-grid">
            <div className="ad-stat-card">
              <div className="ad-stat-icon-wrap primary">
                <span>📦</span>
              </div>
              <div className="ad-stat-info">
                <span className="ad-stat-label">Total Orders</span>
                <span className="ad-stat-val">{totalOrdersCount}</span>
              </div>
            </div>

            <div className="ad-stat-card">
              <div className="ad-stat-icon-wrap warning">
                <span>⏳</span>
              </div>
              <div className="ad-stat-info">
                <span className="ad-stat-label">Pending Confirmation</span>
                <span className="ad-stat-val">{pendingOrdersCount}</span>
              </div>
            </div>

            <div className="ad-stat-card">
              <div className="ad-stat-icon-wrap success">
                <span>✅</span>
              </div>
              <div className="ad-stat-info">
                <span className="ad-stat-label">Completed Orders</span>
                <span className="ad-stat-val">{completedOrdersCount}</span>
              </div>
            </div>

            <div className="ad-stat-card">
              <div className="ad-stat-icon-wrap accent">
                <span>💰</span>
              </div>
              <div className="ad-stat-info">
                <span className="ad-stat-label">Total Sales Value</span>
                <span className="ad-stat-val">₹{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Search & Filter Toolbar */}
          <div className="ad-toolbar-card">
            <div className="ad-search-box">
              <span className="ad-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search by Order ID, Customer name, Phone or City..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="ad-search-input"
              />
              {searchQuery && (
                <button className="ad-clear-btn" onClick={() => setSearchQuery('')}>✕</button>
              )}
            </div>

            <div className="ad-filter-pills">
              <button
                className={`ad-filter-pill ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                All Orders ({totalOrdersCount})
              </button>
              <button
                className={`ad-filter-pill warning ${statusFilter === 'pending' ? 'active' : ''}`}
                onClick={() => setStatusFilter('pending')}
              >
                Pending ({pendingOrdersCount})
              </button>
              <button
                className={`ad-filter-pill success ${statusFilter === 'complete' ? 'active' : ''}`}
                onClick={() => setStatusFilter('complete')}
              >
                Completed ({completedOrdersCount})
              </button>
            </div>
          </div>

          {/* Orders Table & Mobile Cards */}
          <div className="ad-table-card">
            {loading ? (
              <div className="ad-loading-box">
                <div className="ad-spinner"></div>
                <p>Loading orders data...</p>
              </div>
            ) : filteredOrders.length > 0 ? (
              <>
                {/* Desktop View Table */}
                <div className="ad-desktop-table-container">
                  <table className="ad-data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Order ID</th>
                        <th>Date & Time</th>
                        <th>Customer Name</th>
                        <th>Phone</th>
                        <th>Items Count</th>
                        <th>Total Amount</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order, idx) => {
                        const isComplete = order.isComplete || order.status === 'Complete';
                        const totalItems = order.products?.reduce((t: number, p: any) => t + (p.quantity || 0), 0) || 0;
                        const formattedDate = order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-';

                        return (
                          <tr key={order.orderId || idx}>
                            <td className="ad-cell-index">{idx + 1}</td>
                            <td>
                              <span className="ad-order-badge">#{order.orderId}</span>
                            </td>
                            <td className="ad-cell-date">{formattedDate}</td>
                            <td className="ad-cell-customer">
                              <strong>{order.userName || 'Customer'}</strong>
                              {order.city && <span className="ad-customer-city">{order.city}</span>}
                            </td>
                            <td className="ad-cell-phone">
                              {order.phoneNumber ? (
                                <a 
                                  href={`tel:${order.phoneNumber}`}
                                  className="ad-phone-link"
                                >
                                  {order.phoneNumber}
                                </a>
                              ) : '-'}
                            </td>
                            <td>
                              <span className="ad-items-pill">{totalItems} items</span>
                            </td>
                            <td className="ad-cell-price">
                              ₹{typeof order.totalPrice === 'number' ? order.totalPrice.toFixed(2) : order.totalPrice}
                            </td>
                            <td>
                              <span className={`ad-status-pill ${isComplete ? 'complete' : 'pending'}`}>
                                <span className="ad-dot"></span>
                                {isComplete ? 'COMPLETED' : 'PENDING'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="ad-actions-cell">
                                <button 
                                  className="ad-btn-action view" 
                                  onClick={() => setSelectedOrder(order)}
                                  title="View full order details"
                                >
                                  View Details
                                </button>
                                {!isComplete && (
                                  <button
                                    className="ad-btn-action complete"
                                    onClick={() => handleMarkComplete(Number(order.orderId))}
                                    disabled={completingId === Number(order.orderId)}
                                    title="Mark order as completed"
                                  >
                                    {completingId === Number(order.orderId) ? '...' : 'Complete ✓'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile View Card List */}
                <div className="ad-mobile-card-list">
                  {filteredOrders.map((order, idx) => {
                    const isComplete = order.isComplete || order.status === 'Complete';
                    const totalItems = order.products?.reduce((t: number, p: any) => t + (p.quantity || 0), 0) || 0;
                    const formattedDate = order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-IN', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-';

                    return (
                      <div className="ad-mobile-order-card" key={order.orderId || idx} onClick={() => setSelectedOrder(order)}>
                        <div className="ad-m-card-top">
                          <span className="ad-order-badge">#{order.orderId}</span>
                          <span className={`ad-status-pill ${isComplete ? 'complete' : 'pending'}`}>
                            <span className="ad-dot"></span>
                            {isComplete ? 'COMPLETED' : 'PENDING'}
                          </span>
                        </div>

                        <div className="ad-m-card-body">
                          <div className="ad-m-row">
                            <span className="ad-m-label">Customer:</span>
                            <span className="ad-m-val strong">{order.userName || 'Customer'}</span>
                          </div>
                          <div className="ad-m-row">
                            <span className="ad-m-label">Date:</span>
                            <span className="ad-m-val">{formattedDate}</span>
                          </div>
                          <div className="ad-m-row">
                            <span className="ad-m-label">Items:</span>
                            <span className="ad-m-val">{totalItems} products</span>
                          </div>
                          <div className="ad-m-row">
                            <span className="ad-m-label">Total:</span>
                            <span className="ad-m-val price">₹{order.totalPrice}</span>
                          </div>
                        </div>

                        <div className="ad-m-card-footer" onClick={e => e.stopPropagation()}>
                          <button 
                            className="ad-btn-action view full" 
                            onClick={() => setSelectedOrder(order)}
                          >
                            View Details
                          </button>
                          {!isComplete && (
                            <button
                              className="ad-btn-action complete full"
                              onClick={() => handleMarkComplete(Number(order.orderId))}
                              disabled={completingId === Number(order.orderId)}
                            >
                              {completingId === Number(order.orderId) ? 'Updating...' : 'Mark Complete ✓'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="ad-empty-state">
                <div className="ad-empty-icon">📦</div>
                <h3>No Orders Found</h3>
                <p>{searchQuery ? `No orders matched your search "${searchQuery}".` : 'There are no customer orders in this category yet.'}</p>
                {searchQuery && (
                  <button className="ad-filter-pill" onClick={() => setSearchQuery('')}>Clear Search</button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modern Order Details Modal */}
      {selectedOrder && (
        <div className="ad-modal-backdrop" onClick={() => setSelectedOrder(null)}>
          <div className="ad-modal-card" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <div className="ad-modal-title-group">
                <span className="ad-modal-tag">ORDER DETAILS</span>
                <h2>Order #{selectedOrder.orderId}</h2>
              </div>
              <button className="ad-modal-close" onClick={() => setSelectedOrder(null)}>✕</button>
            </div>

            <div className="ad-modal-body">
              {/* Customer & Address Summary Box */}
              <div className="ad-modal-info-grid">
                <div className="ad-modal-info-item">
                  <span className="label">Customer Name</span>
                  <span className="val">{selectedOrder.userName || 'Customer'}</span>
                </div>
                <div className="ad-modal-info-item">
                  <span className="label">Contact Phone</span>
                  <span className="val">
                    {selectedOrder.phoneNumber ? (
                      <a href={`tel:${selectedOrder.phoneNumber}`} className="ad-phone-link">
                        📞 {selectedOrder.phoneNumber}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="ad-modal-info-item full">
                  <span className="label">Customer Address</span>
                  <span className="val">
                    {selectedOrder.address || selectedOrder.streetAddress || 'N/A'}, {selectedOrder.city}, {selectedOrder.state} - {selectedOrder.postalCode || selectedOrder.pinCode || ''}
                  </span>
                </div>
                <div className="ad-modal-info-item">
                  <span className="label">Current Status</span>
                  <span className={`ad-status-pill ${(selectedOrder.isComplete || selectedOrder.status === 'Complete') ? 'complete' : 'pending'}`}>
                    {(selectedOrder.isComplete || selectedOrder.status === 'Complete') ? 'COMPLETED' : 'PENDING ADVANCE'}
                  </span>
                </div>
                <div className="ad-modal-info-item">
                  <span className="label">Order Total</span>
                  <span className="val price">₹{selectedOrder.totalPrice}</span>
                </div>
              </div>

              {/* Items List */}
              <div className="ad-modal-products-header">
                <h3>Ordered Fireworks Products ({selectedOrder.products?.length || 0})</h3>
              </div>

              <div className="ad-modal-products-list">
                {selectedOrder.products && selectedOrder.products.length > 0 ? (
                  selectedOrder.products.map((p: any, pIdx: number) => {
                    const imgSrc = p.productImageUrl ? resolveImageUrl(p.productImageUrl) : null;
                    const itemTotal = Number(p.productRate || 0) * Number(p.quantity || 1);

                    return (
                      <div className="ad-modal-product-item" key={p.productId || pIdx}>
                        <div className="ad-modal-product-thumb">
                          {imgSrc ? (
                            <img src={imgSrc} alt={p.productName} />
                          ) : (
                            <span>🧨</span>
                          )}
                        </div>
                        <div className="ad-modal-product-info">
                          <span className="name">{p.productName}</span>
                          {p.productCategoryName && (
                            <span className="cat">{p.productCategoryName}</span>
                          )}
                          <div className="price-qty">
                            <span>Rate: ₹{Number(p.productRate).toFixed(2)}</span>
                            <span>•</span>
                            <span>Quantity: <strong>{p.quantity}</strong></span>
                          </div>
                        </div>
                        <div className="ad-modal-product-total">
                          ₹{itemTotal.toFixed(2)}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: 16, color: '#64748b', textAlign: 'center' }}>No items data available for this order.</div>
                )}
              </div>

              {/* WhatsApp Quick Link */}
              {selectedOrder.phoneNumber && (
                <div className="ad-modal-wa-box">
                  <a
                    href={`https://wa.me/91${selectedOrder.phoneNumber.replace(/[^0-9]/g, '')}?text=Hello%20${encodeURIComponent(selectedOrder.userName || 'Customer')},%20regarding%20your%20Jai%20Bhavani%20Fireworks%20Order%20%23${selectedOrder.orderId}:`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ad-modal-wa-btn"
                  >
                    💬 Message Customer on WhatsApp
                  </a>
                </div>
              )}
            </div>

            <div className="ad-modal-footer">
              {!selectedOrder.isComplete && selectedOrder.status !== 'Complete' && (
                <button
                  className="ad-btn-action complete"
                  onClick={() => handleMarkComplete(Number(selectedOrder.orderId))}
                  disabled={completingId === Number(selectedOrder.orderId)}
                >
                  {completingId === Number(selectedOrder.orderId) ? 'Updating...' : 'Mark as Complete ✓'}
                </button>
              )}
              <button className="ad-btn-action close" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}